/**
 * Step Indicator Component
 * Feature: 004-restore-missing-features
 * Task: T014
 *
 * Visual progress indicator for multi-step wizards
 */

import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'

export interface Step {
  id: string
  label: string
  description?: string
}

interface StepIndicatorProps {
  steps: Step[]
  currentStep: string
  completedSteps?: string[]
  className?: string
}

export function StepIndicator({
  steps,
  currentStep,
  completedSteps = [],
  className,
}: StepIndicatorProps) {
  const currentIndex = steps.findIndex((s) => s.id === currentStep)

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {steps.map((step, index) => {
        const isCompleted = completedSteps.includes(step.id) || index < currentIndex
        const isCurrent = step.id === currentStep
        const isPending = !isCompleted && !isCurrent

        return (
          <div key={step.id} className="flex items-center gap-2">
            {/* Step circle */}
            <div
              className={cn(
                'flex items-center justify-center rounded-full w-8 h-8 text-sm font-medium transition-colors',
                {
                  'bg-teal-600 text-white': isCurrent,
                  'bg-teal-100 text-teal-700': isCompleted,
                  'bg-gray-100 text-gray-400': isPending,
                }
              )}
            >
              {isCompleted ? (
                <Check className="h-4 w-4" />
              ) : (
                <span>{index + 1}</span>
              )}
            </div>

            {/* Step label (visible on larger screens) */}
            <span
              className={cn('hidden sm:inline text-sm', {
                'text-teal-600 font-medium': isCurrent,
                'text-teal-700': isCompleted,
                'text-gray-400': isPending,
              })}
            >
              {step.label}
            </span>

            {/* Connector line */}
            {index < steps.length - 1 && (
              <div
                className={cn('w-8 h-0.5 mx-1', {
                  'bg-teal-600': isCompleted,
                  'bg-gray-200': !isCompleted,
                })}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
